// React
import React, { createContext, useContext, useState } from 'react';

// Vendors
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';

// Styles
import classes from './Dropdown.module.scss';

// Types
type DropdownProps = {
    children: React.ReactNode;
};

type DropdownButtonProps = {
    children: React.ReactElement;
};

type DropdownTriggerProps = {
    children: (props: { anchorEl: HTMLElement | null; setAnchorEl: (el: HTMLElement | null) => void }) => React.ReactElement;
};

export const DropdownContext = createContext<{
    anchorEl: HTMLElement | null;
    setAnchorEl: (el: HTMLElement | null) => void;
}>({
    anchorEl: null,
    setAnchorEl: () => {},
});

const Dropdown = (props: DropdownProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    return <DropdownContext.Provider value={{ anchorEl, setAnchorEl }}>{props.children}</DropdownContext.Provider>;
};

const DropdownMenu = (props: Omit<MenuProps, 'open'>) => {
    const { anchorEl, setAnchorEl } = useContext(DropdownContext);
    return (
        <Menu
            elevation={0}
            keepMounted
            className={classes.Menu}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            disablePortal
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
        >
            {props.children}
        </Menu>
    );
};

const StyledMenuItem = (props: MenuItemProps) => {
    const { setAnchorEl } = useContext(DropdownContext);

    return (
        <MenuItem
            disableRipple
            className={classes.MenuItem}
            {...props}
            onClick={(e) => {
                setAnchorEl(null);
                props.onClick?.(e);
            }}
        />
    );
};

const DropdownButton = (props: DropdownButtonProps) => {
    const { setAnchorEl } = useContext(DropdownContext);

    const Button = React.cloneElement(props.children, { onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setAnchorEl(e.currentTarget) });

    return Button;
};

const DropdownTrigger = (props: DropdownTriggerProps) => {
    const { anchorEl, setAnchorEl } = useContext(DropdownContext);

    return props.children({ anchorEl, setAnchorEl });
};

export default Object.assign(Dropdown, { Menu: DropdownMenu, Item: StyledMenuItem, Button: DropdownButton, Trigger: DropdownTrigger });
